import * as Business      from '../business';
import { connect }        from 'react-redux';
import { format }         from 'date-fns';
import { getServiceName } from './reviews';
import { parseISO }       from 'date-fns';
import { useState }       from 'react';
import classnames         from 'classnames';
import FetchFromServer    from '../components/fetch_from_server';
import Link               from '../components/link';
import Navbar             from '../components/navbar';
import pluralize          from '../util/pluralize';
import React              from 'react';
import ReviewResponse     from './review_response';
import StarRating         from '../components/star_rating';


const serviceIcons = {
  facebook:    'facebook',
  google:      'google',
  nextdoor:    'nextdoor',
  broadly:     'broadly',
  tripadvisor: 'tripadvisor',
  yelp:        'yelp'
};


export default connect(
  (state, { businessID }) => ({
    businessID,
    data:     Business.getReviews(state, businessID),
    statuses: Business.getReviewsStatuses(state, businessID)
  }),
  (dispatch, { businessID }) => ({
    load:            () => dispatch(Business.readReviews(businessID)),
    respondToReview: ({ reviewID, body }) => dispatch(Business.respondToReview({ reviewID, body }))
  })
)(ReviewsPage);


const BusinessContext = React.createContext();
export function ReviewsPage({ data, businessID, statuses, load, respondToReview }) {
  return (
    <React.Fragment>
      <Navbar title="Reviews"/>
      <FetchFromServer statuses={statuses} load={load}>
        <BusinessContext.Provider value={{ id: businessID }}>
          {data && (<ReviewsComponent {...data} respondToReview={respondToReview}/>)}
        </BusinessContext.Provider>
      </FetchFromServer>
    </React.Fragment>
  );
}

function ReviewsComponent({ reviews, aggregates, respondToReview }) {
  const noAggregates = aggregates
    .filter(a => a.service !== 'broadly' || a.reviewCount > 0)
    .length === 0;

  const noReviews     = reviews.length === 0;
  const requiresSetup = noReviews && noAggregates;

  if (requiresSetup)
    return <SetupRequiredNotice/>;
  else {
    return (
      <div className="reviews block">
        <ReviewsAggregates aggregates={aggregates}/>
        {noReviews ?
          <NoReviews/> :
          <ReviewsMessages {...{ reviews, respondToReview }}/>
        }
      </div>
    );
  }
}


function SetupRequiredNotice() {
  const allIcons = Object.keys(serviceIcons);

  return (
    <div className="setup-required-notice">
      <div className="setup-required-notice__container">
        <div className="setup-required-notice__title">
          You don&apos;t have any reviews yet!
        </div>
        <div className="setup-required-notice__icons">
          {allIcons.map(name => <i key={name} className={`setup-required-notice__icon icon-${name}`}/>)}
        </div>
      </div>
    </div>
  );
}


function NoReviews() {
  return (
    <React.Fragment>
      <div className="headline">
        REVIEW STREAM
      </div>
      <div className="no-reviews">
        <div className="no-reviews__container">
          <div className="no-reviews__title">You don&apos;t have any reviews yet!</div>
          <div className="no-reviews__subtitle">They&apos;ll show up here when they start rolling in!</div>
          <div className="no-reviews__icons">
            <i className="no-reviews__icon icons8 icons8-hand-with-pen"></i>
            <i className="no-reviews__icon icons8 icons8-winner"></i>
            <i className="no-reviews__icon icons8 icons8-megaphone"></i>
          </div>
        </div>
      </div>
    </React.Fragment>
  );
}

function ReviewsAggregates({ aggregates }) {
  return (
    <div className="review-stats-container row">
      {aggregates && (
        aggregates.map((service, key) => <ServiceAggregate key={key} {...service}/>)
      )}
    </div>
  );
}

function ServiceAggregate({ service, reviewCount, rating }) {
  const isNextdoor  = !!(service === 'nextdoor');
  const serviceIcon = `images/icon-${service}-50.png`;

  return (
    <div className="review-stats">
      <img src={serviceIcon} className="review-stats__icon"/>
      <div className="review-stats__content">
        <div className="review-stats__title">{getServiceName(service)}</div>
        {isNextdoor ?
          <NextdoorServiceStat reviewCount={reviewCount}/> :
          <ServiceStat reviewCount={reviewCount} rating={rating}/>
        }
      </div>
    </div>
  );
}


function NextdoorServiceStat({ reviewCount }) {
  const text = reviewCount === 0 ?
    'None yet' :
    `${reviewCount} ${pluralize(reviewCount, 'recommendation')}`;

  return (
    <div className="review-stats__stats">
      <div className="review-stats__stat">{text}</div>
    </div>
  );
}


function ServiceStat({ rating, reviewCount }) {
  return (
    <div className="review-stats__stats">
      {reviewCount > 0 ? (
        <React.Fragment>
          <div className="review-stats__stat">{rating} {pluralize(rating, 'star')}</div>
          <div className="review-stats__stat">{reviewCount} {pluralize(reviewCount, 'review')}</div>
        </React.Fragment>
      ) :
        <div className="review-stats__stat">No reviews yet</div>
      }
    </div>
  );
}


function ReviewsMessages({ reviews, respondToReview }) {
  return (
    <React.Fragment>
      <div className="headline">
        REVIEW STREAM
      </div>
      {reviews.map((review, key) => <ReviewMessage key={key} {...{ review, respondToReview }}/>)}
    </React.Fragment>
  );
}


function ReviewMessage({ review, respondToReview }) {
  const [ response, setReviewResponse ] = useState(review.response);
  const hasProviders                    = review?.providers?.length > 0;
  const providerName                    = hasProviders && review.providers[0].name;
  const transactionDate                 = review?.transactions?.[0]?.timestamp;

  return (
    <div className="review">
      <div className="review__row row">
        <div className="col">
          {review.rating ? <StarRating rating={review.rating}/> : <NoRating/>}
        </div>
        <div className="col">
          <div className="review__published">{format(parseISO(review.published), 'MM/dd/yyyy')}</div>
        </div>
      </div>
      <div className="review__body">{review.body}</div>
      <div className="review__row review__info row">
        <div className="col">
          <ServiceReviewLink service={review.service} link={review.sourceURL}/>
        </div>
        <div className="col">
          <Author author={review.author} customer={review.customer}/>
        </div>
      </div>
      {hasProviders && transactionDate && (
        <div className="review__row review__provider row">
          <div className="col">
          Service provided on {format(parseISO(transactionDate), 'MM/dd/yyyy')} by {providerName}
          </div>
        </div>
      )}
      <ReviewResponse {...{ review, response, setReviewResponse, respondToReview }}/>
    </div>
  );
}

function Author({ author, customer }) {
  return (
    <BusinessContext.Consumer>
      {({ id }) => {
        const href = customer?.conversation?.id ?
          `/${id}/inbox/${customer.conversation.id}` :
          `/${id}/contacts/${customer?.id}`;

        return (
          <div className="review__author">
            {customer && (<a href={href}>{author.name}</a>)}
            {!customer && author.name}
          </div>
        );
      }}
    </BusinessContext.Consumer>
  );
}


function NoRating() {
  return <div>No Rating</div>;
}


function ServiceReviewLink({ service, link }) {
  const serviceName = getServiceName(service);
  const serviceIcon = serviceIcons[service];
  const iconClasses = classnames('link-with-icon__icon', 'icons8', `icons8-${serviceIcon}`);

  return (
    <div className="link-with-icon">
      <Link className="link-with-icon__link" href={link} external>
        <i className={iconClasses}/>{serviceName}
      </Link>
    </div>
  );
}
